import { useState } from 'react';
import { Card, SegmentedControl } from '@salonomy/ui';
import { ReviewSettingsPanel, ReviewsPanel } from '../features/reviews/index.js';
import { useAuth } from '../lib/auth.js';

type Tab = 'reviews' | 'settings';

/**
 * WS13 reviews module page — Reviews | Settings, on the standard screen-scroll / screen-pad / topbar
 * layout (mirrors StockPage).
 *
 * RBAC self-gate (honest, not a hard 403 wall): the LIST read is allowed for ANY staff role, so every
 * authed user reaches the Reviews tab and can SEE the reviews + the salon's rating. Moderation (publish/
 * hide/reply), the settings tab, and requesting a review are manager+ on the API, so:
 *  - `canModerate` is passed into ReviewsPanel — non-managers see the list without the action buttons.
 *  - the Settings tab is only offered to managers; a non-manager who lands on it gets an honest note.
 * This mirrors StockPage's posture: the UI is honest about what a role can do rather than promising an
 * action that would 403.
 */
export function ReviewsPage() {
  const { isManager } = useAuth();
  const [tab, setTab] = useState<Tab>('reviews');

  // Non-managers only ever see the Reviews tab; managers get the segmented Reviews | Settings switch.
  const tabs: Array<{ value: Tab; label: string }> = isManager
    ? [
        { value: 'reviews', label: 'Reviews' },
        { value: 'settings', label: 'Settings' },
      ]
    : [{ value: 'reviews', label: 'Reviews' }];

  return (
    <>
      <div className="topbar">
        <div className="topbar__left">
          <div className="topbar__title">Reviews</div>
        </div>
        {isManager ? (
          <div className="topbar__right">
            <SegmentedControl options={tabs} value={tab} onChange={setTab} />
          </div>
        ) : null}
      </div>

      <div className="screen-scroll">
        <div
          className="screen-pad"
          style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 980 }}
        >
          {!isManager ? (
            <Card>
              <div className="section-title">Reviewing is manager-only</div>
              <div className="muted" style={{ fontSize: 13, marginTop: 8, lineHeight: 1.5 }}>
                You can see every review and the salon&rsquo;s star rating below. Publishing or
                hiding a review, replying publicly, changing review settings, and requesting a
                review are done by an owner or manager. Ask one of them if a review needs attention.
              </div>
            </Card>
          ) : null}

          {tab === 'reviews' || !isManager ? (
            <ReviewsPanel canModerate={isManager} />
          ) : (
            <ReviewSettingsPanel />
          )}
        </div>
      </div>
    </>
  );
}
